import type { ReactNode } from "react";
import { cx } from "./cx";
import s from "./segmented.module.css";

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
  /** Native tooltip, e.g. why an option is disabled. */
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}

/** A group of toggle buttons (`aria-pressed`), as in the reference. Use `role="tablist"` via `tabs`. */
export function SegmentedControl<T extends string>({ options, value, onChange, label, className }: SegmentedControlProps<T>) {
  return (
    <div className={cx(s.seg, className)} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} disabled={o.disabled} title={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Same look, tab semantics: for the 768–1199px work-band switcher. */
export function SegmentedTabs<T extends string>({ options, value, onChange, label, className }: SegmentedControlProps<T>) {
  return (
    <div className={cx(s.seg, className)} role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
